/**
 * Bundle 102 (Phase 0) - useSurveyAutosave.
 *
 * Debounced PATCH with retry banner state. Fires a fire-and-forget
 * health beacon on failure so ops can grep autosave failures per
 * hour without leaking any survey content.
 *
 * Contract:
 *   scheduleAutosave(nextAnswers)  -> debounced PATCH after 600ms
 *   persistAutosave(payload)       -> immediate PATCH; used by Retry
 *   { autosaveFailed, autosavePending, setAutosaveFailed,
 *     setAutosavePending }         -> banner state
 */
import { useCallback, useRef, useState } from 'react';

export const useSurveyAutosave = ({ surveyApi, token, previewOnly }) => {
  const [autosaveFailed, setAutosaveFailed] = useState(false);
  const [autosavePending, setAutosavePending] = useState(null);
  const saveTimer = useRef(null);

  const persistAutosave = useCallback(async (payload) => {
    try {
      await surveyApi.patch(`/survey/${token}`, { answers: payload });
      setAutosaveFailed(false);
      setAutosavePending(null);
      return true;
    } catch (e) {
      setAutosaveFailed(true);
      setAutosavePending(payload);
      try {
        await surveyApi.post(`/survey/${token}/autosave-health`,
          { failure: true }).catch(() => {});
      } catch (_) { /* silent */ }
      return false;
    }
  }, [surveyApi, token]);

  const scheduleAutosave = useCallback((next) => {
    if (previewOnly) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => persistAutosave(next), 600);
  }, [previewOnly, persistAutosave]);

  return {
    autosaveFailed, autosavePending, scheduleAutosave, persistAutosave,
    setAutosaveFailed, setAutosavePending,
  };
};

export default useSurveyAutosave;
